import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  status: "unread" | "read" | "replied" | "archived";
  read_at: string | null;
  replied_at: string | null;
  created_at: string;
  updated_at: string;
  is_spam: boolean;
  spam_reason: string | null;
}

export interface ContactReply {
  id: string;
  message_id: string;
  admin_user_id: string | null;
  body: string;
  email_status: string | null;
  created_at: string;
}

export const useContactMessages = (statusFilter: string = "all") => {
  return useQuery({
    queryKey: ["contact_messages", statusFilter],
    queryFn: async () => {
      let q = supabase
        .from("contact_messages")
        .select("*")
        .order("created_at", { ascending: false });
      if (statusFilter === "spam") {
        q = q.eq("is_spam", true);
      } else {
        q = q.eq("is_spam", false);
        if (statusFilter !== "all") q = q.eq("status", statusFilter);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as ContactMessage[];
    },
    staleTime: 30_000,
  });
};

export const useSpamCount = () => {
  return useQuery({
    queryKey: ["contact_messages", "spam_count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("contact_messages")
        .select("*", { count: "exact", head: true })
        .eq("is_spam", true);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 30_000,
  });
};

export const useSetSpamFlag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isSpam }: { id: string; isSpam: boolean }) => {
      const { error } = await supabase
        .from("contact_messages")
        .update({
          is_spam: isSpam,
          spam_reason: isSpam ? "marque_manuellement" : null,
          status: isSpam ? "read" : "unread",
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact_messages"] });
    },
  });
};


export const useContactReplies = (messageId: string | null) => {
  return useQuery({
    queryKey: ["contact_replies", messageId],
    queryFn: async () => {
      if (!messageId) return [];
      const { data, error } = await supabase
        .from("contact_replies")
        .select("*")
        .eq("message_id", messageId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as ContactReply[];
    },
    enabled: !!messageId,
  });
};

export const useUnreadMessagesCount = () => {
  return useQuery({
    queryKey: ["contact_messages", "unread_count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("contact_messages")
        .select("*", { count: "exact", head: true })
        .eq("status", "unread")
        .eq("is_spam", false);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
};

export const useUpdateMessageStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ContactMessage["status"] }) => {
      const patch: Record<string, unknown> = { status };
      if (status === "read") patch.read_at = new Date().toISOString();
      const { error } = await supabase.from("contact_messages").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact_messages"] });
    },
  });
};

export const useDeleteMessage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("contact_messages").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact_messages"] });
    },
  });
};

export const useSendReply = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      message,
      body,
    }: {
      message: ContactMessage;
      body: string;
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error: insertError } = await supabase.from("contact_replies").insert({
        message_id: message.id,
        admin_user_id: userData.user?.id ?? null,
        body,
      });
      if (insertError) throw insertError;

      const { error: emailError } = await supabase.functions.invoke("send-email", {
        body: {
          type: "contact_reply",
          data: {
            message_id: message.id,
            recipient_name: message.name,
            recipient_email: message.email,
            original_subject: message.subject,
            original_message: message.message,
            reply_body: body,
          },
        },
      });
      if (emailError) throw emailError;

      const { error: updateError } = await supabase
        .from("contact_messages")
        .update({ status: "replied", replied_at: new Date().toISOString() })
        .eq("id", message.id);
      if (updateError) throw updateError;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["contact_messages"] });
      qc.invalidateQueries({ queryKey: ["contact_replies", vars.message.id] });
    },
  });
};
